import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { CRM_SEARCH_MIN_LENGTH, type HubspotCompanySearchHit } from '@bliks-io/contracts';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@bliks-io/ui';

interface CrmSearchProps<T> {
  term: string;
  onTermChange: (term: string) => void;
  placeholder: string;
  /** True only while the FIRST answer for a term is outstanding. */
  isLoading: boolean;
  hits: T[];
  keyOf: (hit: T) => string;
  /** The two lines of a hit; the item provides hover, focus and the keyboard. */
  renderHit: (hit: T) => ReactNode;
  onPick: (hit: T) => void;
  /** A hit that may be shown but not taken. */
  isDisabled?: (hit: T) => boolean;
  /** Nothing may be picked while a pick is being saved. */
  busy?: boolean;
  /** What to say below three characters. */
  minText: string;
  /** What to say when the CRM found nothing. */
  emptyText: string;
  /** An offer that belongs under the empty state, e.g. "create this company". */
  whenEmpty?: ReactNode;
  autoFocus?: boolean;
}

/**
 * One search box over the CRM, with its four states flattened out: too short,
 * searching, nothing found, the hits. Every picker in the account area is this
 * component with its own hit lines, so they look and behave alike — arrow
 * keys, Enter, the same empty state.
 *
 * The filtering is the server's (`shouldFilter={false}`): the list already is
 * the answer to the term.
 */
export function CrmSearch<T>({
  term,
  onTermChange,
  placeholder,
  isLoading,
  hits,
  keyOf,
  renderHit,
  onPick,
  isDisabled,
  busy = false,
  minText,
  emptyText,
  whenEmpty,
  autoFocus
}: CrmSearchProps<T>) {
  const { t } = useTranslation();
  const tooShort = term.trim().length < CRM_SEARCH_MIN_LENGTH;

  return (
    <div className="rounded-md border">
      <Command shouldFilter={false}>
        <CommandInput
          value={term}
          onValueChange={onTermChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
        <CommandList className="max-h-72">
          {tooShort && <Note>{minText}</Note>}
          {!tooShort && isLoading && (
            <Note>
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              {t('accountAdmin.searching')}
            </Note>
          )}
          {!tooShort && !isLoading && hits.length === 0 && (
            <div className="py-6 text-center">
              <p className="text-sm text-muted-foreground">{emptyText}</p>
              {whenEmpty}
            </div>
          )}
          {!tooShort && hits.length > 0 && (
            <CommandGroup>
              {hits.map((hit) => (
                <CommandItem
                  key={keyOf(hit)}
                  value={keyOf(hit)}
                  disabled={busy || isDisabled?.(hit)}
                  onSelect={() => onPick(hit)}
                >
                  <div className="flex w-full flex-col gap-0.5 py-1">{renderHit(hit)}</div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

/** The two lines of a company hit: name with an optional badge, then domain. */
export function CompanyHitLine({
  hit,
  badge
}: {
  hit: HubspotCompanySearchHit;
  badge?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex w-full items-center justify-between gap-2">
        <span className="font-medium">{hit.name ?? t('accountAdmin.unnamed')}</span>
        {badge}
      </div>
      <span className="text-xs text-muted-foreground">{hit.domain ?? hit.companyId}</span>
    </>
  );
}
